import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Copy, QrCode, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/* v85.48 — Halaman admin QR → poster /quick-order-public.
 * Tampilkan QR besar + tombol download PNG. QR mengarah ke URL publik
 * yang bisa diakses customer tanpa login.
 */
const PUBLIC_PATH = "/quick-order-public";

export default function QRQuickOrderPage() {
  const canvasRef = useRef(null);
  const [size, setSize] = useState(512);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}${PUBLIC_PATH}`;

  function downloadPng() {
    try {
      // qrcode.react v4 → <canvas> langsung di DOM
      const wrap = canvasRef.current;
      const canvas = wrap?.querySelector("canvas");
      if (!canvas) { toast.error("QR belum siap"); return; }
      const link = document.createElement("a");
      link.download = `quick-order-qr-${size}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("QR berhasil di-download");
    } catch (e) {
      toast.error("Gagal download: " + e.message);
    }
  }

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL disalin");
    } catch {
      toast.error("Gagal menyalin");
    }
  }

  return (
    <div className="space-y-4 p-4" data-testid="qr-quick-order-page">
      <div className="flex items-center gap-2">
        <QrCode className="w-5 h-5 text-[#E65100]" />
        <h1 className="font-['Manrope'] font-bold text-[18px] text-zinc-900">
          QR Code — Quick Order Publik
        </h1>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* QR Preview */}
        <div className="border border-zinc-200 rounded-sm bg-white p-6 flex flex-col items-center">
          <div ref={canvasRef} className="p-3 bg-white border border-zinc-100" data-testid="qr-canvas-wrap">
            <QRCodeCanvas
              value={url}
              size={size}
              level="H"
              includeMargin
              fgColor="#1f2937"
              bgColor="#ffffff"
            />
          </div>
          <div className="text-[11px] text-zinc-500 mt-2 font-mono break-all text-center" data-testid="qr-url">
            {url}
          </div>
        </div>

        {/* Controls */}
        <div className="border border-zinc-200 rounded-sm bg-white p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
              Tujuan
            </div>
            <div className="text-[13px] text-zinc-800">
              Scan QR ini akan membuka halaman input nomor HP. Setelah customer
              mengisi nomor, mereka langsung diarahkan ke form Quick Order dengan
              alamat riwayat (jika ada) terisi otomatis.
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
              Ukuran QR (px)
            </div>
            <div className="flex gap-1.5">
              {[256, 512, 1024].map(s => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`flex-1 h-9 rounded-sm text-[12px] font-semibold border transition-colors ${
                    size === s
                      ? "bg-[#E65100] text-white border-[#E65100]"
                      : "bg-white text-zinc-600 border-zinc-200 hover:border-[#E65100]"
                  }`}
                  data-testid={`qr-size-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1.5">
              Ukuran besar (1024) lebih tajam saat di-print sebagai poster.
            </div>
          </div>

          <div className="space-y-2">
            <Button
              onClick={downloadPng}
              className="w-full h-10 rounded-sm bg-[#E65100] hover:bg-[#F57C00] gap-2"
              data-testid="qr-download-btn"
            >
              <Download className="w-4 h-4" />
              Unduh PNG ({size}px)
            </Button>
            <Button
              onClick={copyUrl}
              variant="outline"
              className="w-full h-10 rounded-sm gap-2"
              data-testid="qr-copy-url-btn"
            >
              <Copy className="w-4 h-4" />
              Salin URL
            </Button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="w-full h-10 rounded-sm border border-zinc-200 bg-white hover:bg-zinc-50 flex items-center justify-center gap-2 text-[13px] text-zinc-700 font-semibold"
              data-testid="qr-preview-link"
            >
              <ExternalLink className="w-4 h-4" />
              Buka Preview di Tab Baru
            </a>
          </div>
        </div>
      </div>

      {/* Usage tips */}
      <div className="border border-zinc-200 rounded-sm bg-zinc-50 p-4 text-[12px] text-zinc-600 space-y-1.5">
        <div className="font-semibold text-zinc-800 mb-1">Tips Pemasangan:</div>
        <div>• Tempel di meja kasir / depan workshop / stiker mobil service</div>
        <div>• Download versi 1024px untuk cetak poster A4</div>
        <div>• QR ini permanen — tidak perlu di-regenerate</div>
      </div>
    </div>
  );
}
